import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtDate, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { WhatsappShareButton } from "../components/WhatsappShareButton";
import { VoiceRecorder } from "../components/VoiceRecorder";
import { Sparkles, CheckCircle2, MessageSquare, Lock, LockOpen, Camera, History } from "lucide-react";
import { toast } from "sonner";

export default function MeetingDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");

  async function load() {
    const r = await api.get(`/meetings/${id}`);
    setM(r.data); setNotes(r.data?.raw_notes || "");
  }
  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

  const canEdit = m && (user?.role === "committee" || (m.created_by === user?.id && !m.locked));

  async function summarize() {
    if (m?.locked) return toast.error("Unlock the meeting to regenerate minutes");
    setBusy(true);
    try {
      await api.patch(`/meetings/${id}`, { raw_notes: notes });
      await api.post(`/meetings/${id}/summarize`, { raw_notes: notes });
      toast.success("Minutes generated"); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }

  async function saveNotes() {
    try {
      await api.patch(`/meetings/${id}`, { raw_notes: notes });
      toast.success("Notes saved"); load();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function toggleLock() {
    try {
      await api.post(`/meetings/${id}/${m.locked ? "unlock" : "lock"}`);
      toast.success(m.locked ? "Unlocked" : "Locked");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function handleVoice(text) {
    setNotes((n) => (n ? n + "\n\n" : "") + text);
  }

  async function handleImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const up = await api.post("/upload", fd);
      const r = await api.post(`/meetings/${id}/ocr-image`, { file_id: up.data.id });
      setNotes(r.data.raw_notes);
      toast.success("Photo notes extracted");
    } catch (err) { toast.error(formatApiError(err)); }
  }

  if (!m) return <div className="p-10 text-stone-500">Loading…</div>;
  const summary = m.ai_summary;

  return (
    <div className="px-5 md:px-10 py-6 md:py-10 max-w-3xl pb-32">
      <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">{m.type.replace("_", " ")} meeting</div>
      <h1 className="font-serif-display text-3xl md:text-4xl font-semibold mt-1">{m.title}</h1>
      <div className="mt-1 flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-stone-500 flex items-center gap-2">
          {fmtDate(m.date)} · {m.creator_name && <>by {m.creator_name}</>}
          {m.locked && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full"><Lock size={10} /> Locked</span>}
        </div>
        <WhatsappShareButton entityType="meeting" entityId={m.id}
          fallbackText={`*Meeting* — ${m.title} (${fmtDate(m.date)})\n— via NivasOne`}
          testid="wa-share-meeting" className="h-9 px-4 text-xs" label="Share" />
      </div>

      {m.agenda && (
        <div className="mt-5">
          <div className="text-xs font-bold uppercase tracking-wider text-stone-500">Agenda</div>
          <p className="text-stone-800 mt-1.5 whitespace-pre-wrap">{m.agenda}</p>
        </div>
      )}

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold uppercase tracking-wider text-stone-500">Raw notes</div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <VoiceRecorder onTranscript={handleVoice} testid="meeting-voice" />
              <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-300 text-xs hover:border-stone-500" data-testid="meeting-image">
                <Camera size={14} /> Photo of notes
                <input type="file" accept="image/*" capture="environment" hidden onChange={handleImage} />
              </label>
            </div>
          )}
        </div>
        <Textarea rows={6} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" data-testid="meeting-raw" disabled={!canEdit} />
        {canEdit && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={saveNotes} variant="outline" className="rounded-full" data-testid="save-notes">Save notes</Button>
            <Button onClick={summarize} disabled={busy} data-testid="generate-mom"
              className="rounded-full bg-[#C05640] hover:bg-[#A54734]">
              <Sparkles size={16} className="mr-1.5" /> {busy ? "Summarising…" : (summary ? "Regenerate minutes" : "Generate AI minutes")}
            </Button>
            {user?.role === "committee" && (
              <Button onClick={toggleLock} variant="outline" data-testid="toggle-lock" className="rounded-full ml-auto">
                {m.locked ? <><LockOpen size={14} className="mr-1.5" />Unlock</> : <><Lock size={14} className="mr-1.5" />Finalise & lock</>}
              </Button>
            )}
          </div>
        )}
      </div>

      {summary && (
        <div className="mt-7 space-y-5">
          <Section title="Decisions" icon={CheckCircle2}>
            {(summary.decisions || []).map((d, i) => <li key={`${d}-${i}`} className="text-stone-800">{d}</li>)}
            {(!summary.decisions || summary.decisions.length === 0) && <li className="text-stone-500 italic">No specific decisions captured.</li>}
          </Section>
          <Section title="Discussion" icon={MessageSquare}>
            {(summary.discussion || []).map((d, i) => <li key={`${d}-${i}`} className="text-stone-800">{d}</li>)}
          </Section>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2">Action items</div>
            <div className="space-y-2">
              {(summary.action_items || []).map((a, i) => (
                <Link to="/actions" key={`${a.title}-${i}`} className="block bg-white border border-stone-200 rounded-xl p-3 hover:border-stone-400" data-testid={`mom-action-${i}`}>
                  <div className="font-medium text-stone-900">{a.title}</div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    {a.owner ? `Owner: ${a.owner}` : "No owner"} {a.due ? `· due ${a.due}` : ""}
                  </div>
                </Link>
              ))}
              {(!summary.action_items || summary.action_items.length === 0) && <div className="text-sm text-stone-500 italic">No action items.</div>}
            </div>
          </div>
        </div>
      )}

      {m.edit_history?.length > 0 && (
        <div className="mt-7 bg-stone-50 rounded-xl p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2 inline-flex items-center gap-1.5"><History size={12} /> Edit history</div>
          <ul className="text-xs text-stone-600 space-y-1">
            {m.edit_history.slice(-5).map((h, i) => (
              <li key={`${h.at}-${i}`}>{h.by_user_name} · {new Date(h.at).toLocaleString("en-IN")}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-[#FDFBF7] border border-[#E8D8D3] rounded-2xl p-5">
      <div className="text-[11px] font-bold uppercase tracking-widest text-[#C05640] inline-flex items-center gap-1.5 mb-3"><Icon size={14} /> {title}</div>
      <ul className="list-disc pl-5 space-y-1.5 text-sm">{children}</ul>
    </div>
  );
}
